import React from 'react';
import { Wheat, Leaf, Package, Truck, FileText, ArrowRight, ExternalLink } from 'lucide-react';
import ProductVideoPreview from './ProductVideoPreview';
import { useData } from '../contexts/DataContext';
import { useLeadCapture } from '../hooks/useLeadCapture';
import LeadCaptureModal from './LeadCaptureModal';
import { Link, useNavigate } from 'react-router-dom';
import Description from './Description';

// Custom Link component that scrolls to top
const ScrollToTopLink = ({ to, children, className, ...props }: { to: string; children: React.ReactNode; className?: string; [key: string]: any }) => {
  const navigate = useNavigate();
  
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Scroll to top first
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Then navigate after a short delay to ensure scroll completes
    setTimeout(() => {
      navigate(to);
    }, 100);
  };

  return (
    <a href={to} onClick={handleClick} className={className} {...props}>
      {children}
    </a>
  );
};

const Products = () => {
  const { products, loading, pages, blogPosts, settings } = useData();
  const { isModalOpen, selectedProduct, selectedProductImage, openModal, closeModal } = useLeadCapture();

  // Filter only published products
  const publishedProducts = products.filter(product => product.status === 'published');

  // Get product card size from settings
  const productCardSize = settings?.appearance?.productCardSize || 'medium';

  // Get category icons
  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'feed':
        return Wheat;
      case 'supplement':
        return Leaf;
      case 'pellet':
        return Package;
      case 'concentrate':
        return Truck;
      default:
        return Wheat;
    }
  };

  // Get product card classes based on size setting
  const getProductCardClasses = () => {
    return "group bg-gradient-to-br from-gray-50 to-amber-50 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] cursor-pointer";
  };

  // Get product card content classes based on size setting
  const getProductContentClasses = () => {
    switch (productCardSize) {
      case 'small':
        return {
          container: "p-4",
          title: "text-lg font-bold text-amber-800",
          description: "text-sm text-gray-700 mb-4 leading-relaxed",
          features: "space-y-2 mb-4",
          price: "text-lg font-bold text-amber-600",
          button: "w-full bg-gradient-to-r from-amber-500 to-yellow-500 text-white py-2 px-4 rounded-lg font-semibold hover:from-amber-600 hover:to-yellow-600 transition-colors duration-200 hover:shadow-lg text-sm"
        };
      case 'large':
        return {
          container: "p-10",
          title: "text-3xl font-bold text-amber-800",
          description: "text-lg text-gray-700 mb-8 leading-relaxed",
          features: "space-y-4 mb-8",
          price: "text-3xl font-bold text-amber-600",
          button: "w-full bg-gradient-to-r from-amber-500 to-yellow-500 text-white py-4 px-8 rounded-lg font-semibold hover:from-amber-600 hover:to-yellow-600 transition-colors duration-200 hover:shadow-lg text-lg"
        };
      default: // medium
        return {
          container: "p-8",
          title: "text-2xl font-bold text-amber-800",
          description: "text-gray-700 mb-6 leading-relaxed",
          features: "space-y-3 mb-6",
          price: "text-2xl font-bold text-amber-600",
          button: "w-full bg-gradient-to-r from-amber-500 to-yellow-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-amber-600 hover:to-yellow-600 transition-colors duration-200 hover:shadow-lg"
        };
    }
  };

  // Find related content for a product
  const getRelatedContent = (slugs: string[] = []) => {
    if (!slugs || slugs.length === 0) return [];

    const relatedContent = [];
    
    // Check pages
    for (const slug of slugs) {
      const page = pages.find(p => p.slug === slug && p.status === 'published');
      if (page) {
        relatedContent.push({
          title: page.title,
          slug: page.slug,
          type: 'page'
        });
        continue;
      }
      
      // Check blog posts
      const post = blogPosts.find(p => p.slug === slug && p.status === 'published');
      if (post) {
        relatedContent.push({
          title: post.title,
          slug: post.slug,
          type: 'blog'
        });
      }
    }
    
    return relatedContent;
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleGetQuote = (productName: string, productImage?: string) => {
    openModal(productName, productImage);
  };

  // Show sample products if no products are loaded from database
  const sampleProducts = [
    {
      id: '1',
      name: 'Cotton Seed Cake',
      category: 'Feed',
      description: 'High-protein cattle feed made from premium cotton seeds. Rich in essential nutrients for enhanced milk production and improved cattle health. Learn more at https://example.com/cotton-seed-benefits or visit www.cattlefeedguide.com for detailed information.',
      image: 'https://images.pexels.com/photos/2733918/pexels-photo-2733918.jpeg',
      videoUrl: 'https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4',
      features: ['High Protein Content (20-25%)', 'Rich in Essential Amino Acids', 'Improves Milk Quality', 'Cost-Effective Solution'],
      isFeatured: true,
      status: 'published',
      price: 45,
      related_page_slugs: ['benefits-cotton-seed-cake-dairy-cattle']
    },
    {
      id: '2',
      name: 'Premium Khal',
      category: 'Supplement',
      description: 'Nutritious oil cake feed supplement that boosts digestive health and overall cattle performance with natural ingredients. Check our detailed guide at https://example.com/khal-supplement-guide for optimal usage.',
      image: 'https://images.pexels.com/photos/1459505/pexels-photo-1459505.jpeg',
      videoUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      features: ['Natural Oil Content', 'Enhanced Digestibility', 'Immune System Support', 'Organic Certification'],
      isFeatured: true,
      status: 'published',
      price: 38,
      related_page_slugs: ['proper-feed-storage-techniques']
    },
    {
      id: '3',
      name: 'Pellet Feed',
      category: 'Pellet',
      description: 'Specially formulated pelletized feed for optimal nutrition delivery and easy feeding with consistent quality. Visit www.pelletfeedinfo.com for comprehensive feeding guidelines.',
      image: 'https://images.pexels.com/photos/158063/bellingrath-gardens-alabama-landscape-scenic-158063.jpeg',
      videoUrl: null,
      features: ['Balanced Nutrition', 'Easy to Store & Feed', 'Reduced Waste', 'Consistent Quality'],
      isFeatured: false,
      status: 'published',
      price: 52,
      related_page_slugs: []
    },
    {
      id: '4',
      name: 'Dairy Concentrate',
      category: 'Concentrate',
      description: 'Complete nutritional solution designed specifically for high-yield dairy cattle with scientific formulation. Download our comprehensive guide at https://example.com/dairy-concentrate-guide for best practices.',
      image: 'https://images.pexels.com/photos/422218/pexels-photo-422218.jpeg',
      videoUrl: 'https://vimeo.com/123456789',
      features: ['Complete Mineral Mix', 'Vitamin Enriched', 'Lactation Support', 'Scientific Formulation'],
      isFeatured: true,
      status: 'published',
      price: 65,
      related_page_slugs: []
    }
  ];

  const displayProducts = publishedProducts.length > 0 ? publishedProducts : sampleProducts;

  if (loading) {
    return (
      <section id="products" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-amber-800 mb-6">
              Our Premium Products
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Discover our comprehensive range of high-quality cattle feed and nutritional 
              supplements designed to maximize dairy productivity and animal health.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-gradient-to-br from-gray-50 to-amber-50 rounded-2xl overflow-hidden shadow-lg animate-pulse">
                <div className="h-64 bg-gray-300"></div>
                <div className="p-8">
                  <div className="h-6 bg-gray-300 rounded mb-4"></div>
                  <div className="h-4 bg-gray-300 rounded mb-6"></div>
                  <div className="space-y-2 mb-6">
                    <div className="h-3 bg-gray-300 rounded"></div>
                    <div className="h-3 bg-gray-300 rounded"></div>
                  </div>
                  <div className="h-12 bg-gray-300 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section id="products" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-amber-800 mb-6">
              Our Premium Products
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Discover our comprehensive range of high-quality cattle feed and nutritional 
              supplements designed to maximize dairy productivity and animal health.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {displayProducts.map((product) => {
              const IconComponent = getCategoryIcon(product.category);
              const relatedContent = getRelatedContent(product.related_page_slugs);
              
              // Debug logging for product data
              console.log('Rendering product:', {
                id: product.id,
                name: product.name,
                image: product.image,
                videoUrl: product.videoUrl,
                hasVideoUrl: Boolean(product.videoUrl)
              });
              
              const { title, description, features, price, button, container } = getProductContentClasses();

              return (
                <div 
                  key={product.id} 
                  className={getProductCardClasses()}
                  onClick={() => handleGetQuote(product.name, product.image)}
                >
                  <div className="relative h-64 overflow-hidden">
                    <ProductVideoPreview 
                      imageUrl={product.image} 
                      videoUrl={product.videoUrl}
                      alt={product.name}
                      className="w-full h-full group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    <div className="absolute top-4 left-4">
                      <div className="bg-white/90 backdrop-blur-sm p-3 rounded-full">
                        <IconComponent className="text-amber-600" size={24} />
                      </div>
                    </div>
                    {product.isFeatured && (
                      <div className="absolute top-4 right-4">
                        <div className="bg-amber-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                          Featured
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className={container}>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className={title}>{product.name}</h3>
                      <span className="text-sm font-medium text-amber-600 bg-amber-100 px-3 py-1 rounded-full">
                        {product.category}
                      </span>
                    </div>
                    <Description content={product.description} />
                    
                    {product.features && product.features.length > 0 && (
                      <div className={features}>
                        <h4 className="font-semibold text-amber-700">Key Features:</h4>
                        <ul className="space-y-2">
                          {product.features.slice(0, 4).map((feature, idx) => (
                            <li key={idx} className="flex items-center space-x-3">
                              <div className="w-2 h-2 bg-amber-500 rounded-full flex-shrink-0"></div>
                              <span className="text-gray-600 text-sm">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Related Content */}
                    {relatedContent.length > 0 && (
                      <div className="mb-6">
                        <h4 className="font-semibold text-amber-700 mb-3">Related Articles:</h4>
                        <ul className="space-y-2">
                          {relatedContent.map((content, idx) => (
                            <li key={idx}>
                              <ScrollToTopLink
                                to={`/${content.slug}`}
                                className="flex items-center space-x-2 text-amber-600 hover:text-amber-800 transition-colors"
                              >
                                <FileText size={16} />
                                <span className="text-sm">{content.title}</span>
                                <ArrowRight size={14} className="ml-1" />
                              </ScrollToTopLink>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {product.price && (
                      <div className="mb-6">
                        <span className={price}>₹{product.price}</span>
                        <span className="text-gray-500 ml-2">per kg</span>
                      </div>
                    )}
                    
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGetQuote(product.name, product.image);
                      }}
                      className={button}
                    >
                      Get Quote
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Additional Info */}
          <div className="mt-16 bg-gradient-to-r from-amber-600 to-yellow-600 rounded-2xl p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-4">Custom Formulations Available</h3>
            <p className="text-lg mb-6 opacity-90">
              Need a specific nutritional blend? Our experts can create custom formulations 
              tailored to your livestock's unique requirements.
            </p>
            <button 
              onClick={scrollToContact}
              className="bg-white text-amber-600 px-8 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors duration-200"
            >
              Contact Our Experts
            </button>
          </div>
        </div>
      </section>

      {/* Lead Capture Modal */}
      <LeadCaptureModal
        isOpen={isModalOpen}
        onClose={closeModal}
        productName={selectedProduct}
        productImage={selectedProductImage}
      />
    </>
  );
};

export default Products;
